'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react'

export type CatalogUrlState = {
	pathname: string
	search: string
	resolvedUrl: string
}

export type CatalogUrlContextValue = CatalogUrlState & {
	updateResolvedUrl: (nextResolvedUrl: string) => void
}

const CatalogUrlContext = createContext<CatalogUrlContextValue | null>(null)

export const useCatalogUrl = (): CatalogUrlContextValue | null => useContext(CatalogUrlContext)

export const getCatalogUrlParts = (resolvedUrl: string): CatalogUrlState => {
	if (!resolvedUrl) {
		return { pathname: '', search: '', resolvedUrl: '' }
	}

	if (resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://')) {
		try {
			const url = new URL(resolvedUrl)
			return {
				pathname: url.pathname,
				search: url.search.slice(1),
				resolvedUrl: `${url.pathname}${url.search}`
			}
		} catch {}
	}

	const [pathname = '', rawSearch = ''] = resolvedUrl.split('?')
	const search = rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch

	return {
		pathname,
		search,
		resolvedUrl: `${pathname}${search ? `?${search}` : ''}`
	}
}

export const normalizeResolvedUrl = (resolvedUrl: string): string => getCatalogUrlParts(resolvedUrl).resolvedUrl

interface CatalogUrlProviderProps {
	/** resolvedUrl, вычисленный на SSR (для консистентной гидрации). */
	initialResolvedUrl?: string
	children: ReactNode
}

/**
 * Самодостаточный провайдер URL каталога с shallow-обновлением.
 *
 * updateResolvedUrl меняет адресную строку через history.replaceState БЕЗ навигации Next —
 * страница-серверный компонент не пересчитывается, экран не ремаунтится (drawer/скролл
 * сохраняются). Фильтр/товары раздела рефетчатся, читая resolvedUrl из этого контекста.
 * SSR/SEO для прямых заходов и краулеров не меняется — они по-прежнему рендерят /filter/-маршрут.
 */
export const CatalogUrlProvider: FC<CatalogUrlProviderProps> = ({ initialResolvedUrl, children }) => {
	const pathname = usePathname()
	const searchParams = useSearchParams()
	const ignoreResolvedSyncRef = useRef(false)

	const getResolvedUrlFromRouter = (): string =>
		`${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

	const [resolvedUrl, setResolvedUrl] = useState(initialResolvedUrl || getResolvedUrlFromRouter())
	const { pathname: resolvedPathname, search: resolvedSearch } = getCatalogUrlParts(resolvedUrl)

	const updateResolvedUrl = (nextUrl: string): void => {
		const normalizedUrl = normalizeResolvedUrl(nextUrl)
		if (resolvedUrl === normalizedUrl) return

		ignoreResolvedSyncRef.current = true
		setResolvedUrl(normalizedUrl)
		window.history.replaceState(null, '', normalizedUrl)
	}

	// Синхронизация с реальной навигацией (клик по ссылке, кнопка «назад» браузера):
	// pathname/searchParams меняются — подхватываем в resolvedUrl. Собственные shallow-обновления
	// пропускаем через ignoreResolvedSyncRef, чтобы не перетереть только что выставленный URL.
	useEffect(() => {
		const nextResolvedUrl = getResolvedUrlFromRouter()
		if (ignoreResolvedSyncRef.current) {
			ignoreResolvedSyncRef.current = false
			return
		}
		if (resolvedUrl !== nextResolvedUrl) {
			setResolvedUrl(nextResolvedUrl)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, searchParams, resolvedUrl])

	return (
		<CatalogUrlContext.Provider
			value={{ pathname: resolvedPathname, search: resolvedSearch, resolvedUrl, updateResolvedUrl }}>
			{children}
		</CatalogUrlContext.Provider>
	)
}
